// The way between the map and a tile's interior (PRD sections 14.2 and 19.4): it should feel like
// leaning over the model, not like changing screens. Entering, the map's camera dives towards the
// tile and tilts towards the interior's angle while the neighbouring tiles fade into the sea; then
// the interior rises from a little further and lower to its own framing as the fade clears. Leaving
// runs the same way back. It lasts about a second and only steers the camera and a fade.
import { easeInOutCubic, lerp, lerpPose, type Pose } from '../render/camera-rig';

export type Direction = 'enter' | 'leave';

/** Length of the first half (the level we leave) and of the second (the level we arrive at), in ms. */
export const HALF_MS = { first: 450, second: 550 } as const;

export interface Flight {
  readonly direction: Direction;
  readonly start: number;
  /** Camera of the level we leave: where it starts and where it ends at the switch. */
  readonly leaveFrom: Pose;
  readonly leaveTo: Pose;
  /** Camera of the level we arrive at: where it starts at the switch and where it rests. */
  readonly arriveFrom: Pose;
  readonly arriveTo: Pose;
}

export interface FlightFrame {
  /** 'leave' while the camera still shows the level we leave, 'arrive' after the switch. */
  readonly stage: 'leave' | 'arrive';
  readonly pose: Pose;
  /** How much the neighbourhood is faded out, from 0 to 1. */
  readonly fade: number;
  readonly done: boolean;
}

/** Where a flight is at `now`. */
export function flightAt(flight: Flight, now: number): FlightFrame {
  const t = now - flight.start;
  if (t < HALF_MS.first) {
    const k = easeInOutCubic(t / HALF_MS.first);
    return { stage: 'leave', pose: lerpPose(flight.leaveFrom, flight.leaveTo, k), fade: k, done: false };
  }
  const u = Math.min(1, (t - HALF_MS.first) / HALF_MS.second);
  const k = easeInOutCubic(u);
  return { stage: 'arrive', pose: lerpPose(flight.arriveFrom, flight.arriveTo, k), fade: lerp(1, 0, Math.min(1, u * 1.6)), done: u >= 1 };
}

/** The map camera's dive: over the tile, closer, and tilted part of the way to the interior's angle. */
export function diveTowards(from: Pose, x: number, y: number, z: number, interiorPitch: number, closeness: number): Pose {
  return { x, y, z, distance: from.distance * closeness, yaw: from.yaw, pitch: lerp(from.pitch, interiorPitch, 0.6), shiftY: 0 };
}

/** The interior camera's start: further away and lower, as if seen from the map. */
export function riseFrom(rest: Pose, mapPitch: number, farness: number): Pose {
  return { ...rest, distance: rest.distance * farness, pitch: lerp(rest.pitch, mapPitch, 0.6) };
}
